import { createClient } from '@supabase/supabase-js'

// Create a custom error for supabase client issues
class SupabaseInitError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'SupabaseInitError';
  }
}

// Initialize Supabase client with more robust configuration
export const createSupabaseClient = () => {
  if (!process.env.NEXT_PUBLIC_SUPABASE_URL) {
    console.error('Missing NEXT_PUBLIC_SUPABASE_URL environment variable');
    throw new SupabaseInitError('Supabase URL not configured');
  }

  if (!process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY) {
    console.error('Missing NEXT_PUBLIC_SUPABASE_ANON_KEY environment variable');
    throw new SupabaseInitError('Supabase anon key not configured');
  }

  try {
    return createClient(
      process.env.NEXT_PUBLIC_SUPABASE_URL,
      process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY,
      {
        auth: {
          persistSession: true,
          storageKey: 'supabase.auth.token',
          autoRefreshToken: true,
          detectSessionInUrl: true,
        },
      }
    );
  } catch (error) {
    console.error('Failed to initialize Supabase client:', error);
    throw new SupabaseInitError('Failed to initialize Supabase client');
  }
};

// Initialize the client with better error handling
let supabaseClient;
try {
  supabaseClient = createSupabaseClient();
  console.log('Supabase client initialized successfully');
} catch (error) {
  const initError = error instanceof SupabaseInitError ? error : new SupabaseInitError('Unknown Supabase initialization error');
  console.error('Error initializing Supabase client:', initError);
  
  // Create a fallback client that throws the initialization error when used
  // This ensures type compatibility while preventing unexpected behavior
  const errorThrower = (methodName: string) => () => {
    console.error(`Attempted to use Supabase client method '${methodName}' after initialization failed.`);
    throw initError; 
  };

  supabaseClient = {
    auth: {
      getSession: errorThrower('auth.getSession'),
      onAuthStateChange: errorThrower('auth.onAuthStateChange'),
      signInWithPassword: errorThrower('auth.signInWithPassword'),
      signUp: errorThrower('auth.signUp'),
      signOut: errorThrower('auth.signOut'),
      // Add other auth methods used in your app if necessary
    },
    from: (table: string) => ({
      select: errorThrower(`from('${table}').select`),
      insert: errorThrower(`from('${table}').insert`),
      update: errorThrower(`from('${table}').update`),
      delete: errorThrower(`from('${table}').delete`),
      upsert: errorThrower(`from('${table}').upsert`),
      // Mock builder methods to maintain chainability for types
      eq: errorThrower(`from('${table}').eq`), 
      neq: errorThrower(`from('${table}').neq`), 
      gt: errorThrower(`from('${table}').gt`), 
      gte: errorThrower(`from('${table}').gte`), 
      lt: errorThrower(`from('${table}').lt`), 
      lte: errorThrower(`from('${table}').lte`), 
      like: errorThrower(`from('${table}').like`), 
      ilike: errorThrower(`from('${table}').ilike`), 
      is: errorThrower(`from('${table}').is`), 
      in: errorThrower(`from('${table}').in`), 
      contains: errorThrower(`from('${table}').contains`), 
      containedBy: errorThrower(`from('${table}').containedBy`), 
      rangeGt: errorThrower(`from('${table}').rangeGt`), 
      rangeGte: errorThrower(`from('${table}').rangeGte`), 
      rangeLt: errorThrower(`from('${table}').rangeLt`), 
      rangeLte: errorThrower(`from('${table}').rangeLte`), 
      rangeAdjacent: errorThrower(`from('${table}').rangeAdjacent`), 
      overlaps: errorThrower(`from('${table}').overlaps`), 
      textSearch: errorThrower(`from('${table}').textSearch`), 
      match: errorThrower(`from('${table}').match`), 
      not: errorThrower(`from('${table}').not`), 
      or: errorThrower(`from('${table}').or`), 
      filter: errorThrower(`from('${table}').filter`), 
      limit: errorThrower(`from('${table}').limit`), 
      order: errorThrower(`from('${table}').order`), 
      range: errorThrower(`from('${table}').range`), 
      single: errorThrower(`from('${table}').single`), 
      maybeSingle: errorThrower(`from('${table}').maybeSingle`), 
      csv: errorThrower(`from('${table}').csv`), 
    }),
    rpc: errorThrower('rpc'),
    // Add other top-level Supabase methods if needed (e.g., storage)
    storage: {
      from: (bucketId: string) => ({
        upload: errorThrower(`storage.from('${bucketId}').upload`),
        download: errorThrower(`storage.from('${bucketId}').download`),
        list: errorThrower(`storage.from('${bucketId}').list`),
        // Add other storage methods as needed
      }),
    }
  } as any; // Use 'as any' here to bypass strict type checking for the mock
  // It's crucial that the actual createSupabaseClient returns a correctly typed client.
}

// Ensure the exported type is SupabaseClient
export const supabase: ReturnType<typeof createSupabaseClient> = supabaseClient; 